import { can } from '../../../lib/permissions';
import { hasNoPermissions } from '../../../config/routePermissions';
import type { Menu, Submenu } from './types';

export interface MenuVisibilityContext {
    /** Privilegios del administrador de centro. */
    privileges: number[];
    /** Módulos contratados por el centro. */
    modules: string[];
    /** Modo "zero" (centro sin suscripción): oculta lo que no puede usar. */
    zero: boolean;
    /** En producción se ocultan las entradas marcadas como beta. */
    isProduction: boolean;
    /**
     * Roles v8 encendidos (flag `manager-roles-permissions`): la entrada se
     * decide por su `permission` y no por su `privilege`.
     */
    rolesEnabled?: boolean;
    /** Códigos de permiso v8 del operador. */
    permissions?: readonly string[];
}

/** Menús de primer nivel que no tienen sentido en modo zero. */
const ZERO_HIDDEN_MENU_IDS = ['events', 'scReports', 'activities'];

/** Submenús que no tienen sentido en modo zero. */
const ZERO_HIDDEN_SUBMENU_PATHS = ['remittences', 'scTickets'];
const ZERO_HIDDEN_SUBMENU_MODULES = ['device'];

/**
 * Con los roles encendidos manda el permiso v8 de la entrada; con ellos
 * apagados, el privilegio numérico de siempre.
 */
function hasAccess(
    entry: Pick<Submenu, 'privilege' | 'permission'>,
    {
        privileges,
        rolesEnabled = false,
        permissions = [],
    }: MenuVisibilityContext,
): boolean {
    if (rolesEnabled) {
        return (
            !entry.permission ||
            can({ rolesEnabled, permissions }, entry.permission)
        );
    }
    return !entry.privilege || privileges.includes(entry.privilege);
}

function isAllowed(
    entry: Pick<Submenu, 'privilege' | 'permission' | 'module' | 'beta'>,
    context: MenuVisibilityContext,
): boolean {
    const { modules, isProduction } = context;
    if (!hasAccess(entry, context)) return false;
    if (entry.module && !modules.includes(entry.module)) return false;
    // Las entradas beta no se muestran en producción hasta que el front nuevo
    // esté disponible para todos.
    if (entry.beta && isProduction) return false;
    return true;
}

function isSubmenuVisible(
    submenu: Submenu,
    context: MenuVisibilityContext,
): boolean {
    if (!isAllowed(submenu, context)) return false;

    if (context.zero) {
        if (submenu.path && ZERO_HIDDEN_SUBMENU_PATHS.includes(submenu.path)) {
            return false;
        }
        if (
            submenu.module &&
            ZERO_HIDDEN_SUBMENU_MODULES.includes(submenu.module)
        ) {
            return false;
        }
    }

    return true;
}

/**
 * Menús visibles para el centro y el administrador actuales. Aplica las mismas
 * reglas que tenía la cabecera antigua (privilegios, módulos y modo zero), con
 * los permisos v8 en lugar de los privilegios si los roles están encendidos, y
 * descarta los menús que se quedan sin submenús. No muta la entrada.
 */
export function filterVisibleMenus(
    menus: Menu[],
    context: MenuVisibilityContext,
): Menu[] {
    // Sin ningún permiso (sin rol o con el rol desactivado) no se enseña
    // ninguna sección, ni siquiera las que no exigen permiso (Documentos): el
    // operador sólo ve la pantalla de "sin acceso".
    if (
        hasNoPermissions({
            rolesEnabled: !!context.rolesEnabled,
            permissions: context.permissions ?? [],
        })
    ) {
        return [];
    }

    return menus
        .filter((menu) => isAllowed(menu, context))
        .filter(
            (menu) => !(context.zero && ZERO_HIDDEN_MENU_IDS.includes(menu.id)),
        )
        .map((menu) =>
            menu.submenus
                ? {
                      ...menu,
                      submenus: menu.submenus.filter((submenu) =>
                          isSubmenuVisible(submenu, context),
                      ),
                  }
                : menu,
        )
        .filter((menu) => !menu.submenus || menu.submenus.length > 0);
}
